import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';
import type { SearchWord } from 'api/methods/SearchWordApi';
import SearchWordApi from 'api/methods/SearchWordApi';

/**
 * [SearchWord] Query key 생성
 * --
 */
export const searchWordQueryKeys = createQueryKeys('searchWord', {
  getSearchWord: (idx: number, params?: { [key: string]: string | number }) => [
    idx,
    params,
  ],
  getSearchWords: (params?: { [key: string]: string | number }) => [params],
});

/**
 * [SearchWord] 추천 검색어 전체 조회
 * --
 */
export const useGetSearchWords = (
  params?: { [key: string]: string | number },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: searchWordQueryKeys.getSearchWords(params).queryKey,
    queryFn: () => SearchWordApi.getSearchWords(params),
    ...options,
  });
  return data;
};

/**
 * [SearchWord] 추천 검색어 상세 조회
 * --
 */
export const useGetSearchWord = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: searchWordQueryKeys.getSearchWord(idx, params).queryKey,
    queryFn: () => SearchWordApi.getSearchWord(idx, params),
  });

  return data;
};

/**
 * [SearchWord] 추천 검색어 생성
 * --
 */
export const useCreateSearchWord = (body: SearchWord) => {
  const { mutateAsync: createSearchWord, ...result } = useMutation({
    mutationFn: (variables: SearchWord) =>
      SearchWordApi.createSearchWord(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createSearchWord, ...result };
};

/**
 * [SearchWord] 추천 검색어 수정
 * --
 */
export const useUpdateSearchWord = (body: SearchWord) => {
  const { mutateAsync: updateSearchWord, ...result } = useMutation({
    mutationFn: (variables: SearchWord) =>
      SearchWordApi.updateSearchWord(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { updateSearchWord, ...result };
};

/**
 * [SearchWord] 추천 검색어 삭제
 * --
 */
export const useDeleteSearchWord = (idx: number) => {
  const { mutateAsync: deleteSearchWord, ...result } = useMutation({
    mutationFn: (variables: number) =>
      SearchWordApi.deleteSearchWord(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { deleteSearchWord, ...result };
};

/**
 * [SearchWord] 추천 검색어 일괄 생성/수정
 * --
 */
export const useUpsertSearchWord = () => {
  const { mutateAsync: upsertSearchWord, ...result } = useMutation({
    mutationFn: (variables: SearchWord[]) =>
      SearchWordApi.upsertSearchWord(variables),
  });
  return { useUpsertSearchWord, ...result };
};
